import { mapGetters } from 'vuex'

import { throttle } from 'lodash'
import MarkdownIt from 'markdown-it'
import MarkdownItAnchor from 'markdown-it-anchor'
import MarkdownItContainer from 'markdown-it-container'
import { full as emoji } from 'markdown-it-emoji'
import MarkdownItLinkAttributes from 'markdown-it-link-attributes'
import MarkdownItReplaceLink from 'markdown-it-replace-link'
import Prism from 'prismjs'
import slugify from 'slugify'

import 'prismjs/components/prism-http.js'
import 'prismjs/components/prism-json.js'
import 'prismjs/components/prism-markup.js'

import { getSiteURL, handleCodeCopyClick, isDocumentAvailable, joinURLPath, prepareMarkdownContent } from '@sigma-frontend-libs/commons'

import { globalErrorHandler, routerParamsReplace } from '@/utils'

import { className, documentationLevels } from '@/components/services/documentation/utils'

import render from './render'

const removeClass = selector => {
  const elements = document.querySelectorAll(`.${selector}`)
  if (elements.length > 0) {
    for (const element of elements) {
      if (document.contains(element)) {
        element.classList.remove(selector)
      }
    }
  }
}

const toSlag = slug => {
  slug = slugify(slug, {
    replacement: '-',
    strict: true,
    lower: true
  })

  return slug
}

export default {
  name: 'Template',

  mixins: [ documentationLevels ],

  data() {
    return {
      headings: [],
      slugs: [],

      md: undefined,
      html: undefined,

      scrollTop: 0,

      loading: false
    }
  },

  computed: {
    ...mapGetters({ token: 'authentication/token' }),

    defaultDocument() {
      return {
        category: this.levels[0].category,
        file: this.levels[0].files[0]
      }
    },
    currentDocument() {
      const { category, file } = this.$route.params
      if (!category || !file) {
        return this.defaultDocument
      }

      return {
        category,
        file
      }
    }
  },

  watch: {
    $route: {
      async handler(to, from) {
        const id = to.query.id || from.query.id
        if (id) {
          this.scrollTo(id)
        }
        if (to.params.file !== from.params.file) {
          await this.getDocument()
        }
      },
      deep: true
    },

    locale() {
      this.getDocument()
    }
  },

  async mounted() {
    await this.init()
    await this.getDocument()

    if (this.$refs.body) {
      this.$refs.body.onscroll = throttle(this.onScroll, 100)
    }

    document.addEventListener('click', handleCodeCopyClick)
  },

  methods: {
    async init() {
      this.md = await new Promise((resolve, reject) => {
        try {
          const md = new MarkdownIt({
            html: true,
            linkify: true,
            typographer: true,

            highlight(code, lang) {
              const source = Prism.highlight(code, Prism.languages[lang] || Prism.languages.markup, lang)

              return `<pre class="language-${lang || 'markup'}"><code class="language-${lang || 'markup'}">${source}</code></pre>`
            },

            replaceLink: link => {
              const linkRegExp = /^#(.+)/
              link = decodeURIComponent(link)
              if (linkRegExp.test(link)) {
                link = `#${this.$route.path}?id=${toSlag(link.replace(linkRegExp, '$1'))}`
              }

              return link
            }
          })
          md.linkify.set({ fuzzyLink: true })

          const containerOptions = value => {
            const containerClassMatchRegExp = new RegExp(`^${value}(\\s+.*)?$`)

            return {
              validate: params => {
                return params.trim().match(containerClassMatchRegExp)
              },
              render: (tokens, idx) => {
                if (tokens[idx].nesting === 1) {
                  return `<div class="${value}">\n`
                } else {
                  return '</div>\n'
                }
              }
            }
          }
          for (const item of [ 'tip', 'warning', 'danger' ]) {
            md.use(MarkdownItContainer, item, containerOptions(item))
          }

          md.use(MarkdownItAnchor, {
            level: [ 2, 3, 4, 5, 6 ],
            slugify: toSlag,
            permalink: MarkdownItAnchor.permalink.linkInsideHeader({
              symbol: '#',
              placement: 'before',
              renderHref: slug => {
                return `#${this.$route.path}?id=${slug}`
              },
              space: true,
              class: `${className}-anchor`
            }),
            callback: (token, info) => {
              this.slugs.push(info.slug)
              this.headings.push({
                level: Number(token.tag.substring(1)),
                slug: info.slug,
                title: info.title
              })
            }
          })
          md.use(MarkdownItReplaceLink)
          md.use(MarkdownItLinkAttributes, {
            matcher: href => {
              return href.startsWith('https')
            },
            attrs: { target: '_self' }
          })
          md.use(emoji)

          resolve(md)
        } catch (error) {
          reject(error)
        }
      })
    },
    async render(category, filename) {
      try {
        this.loading = true

        const pathToFile = joinURLPath('docs', this.locale, category, `${filename}.md`)
        const fileDir = pathToFile.replace(/\/[^/]*$/, '')

        this.headings = []
        this.slugs = []

        const response = await fetch(getSiteURL(pathToFile), { cache: 'no-cache' })
        if (!response.ok) {
          throw new Error(response.status)
        }
        let text = await response.text()
        if (text.startsWith('<!DOCTYPE html>')) {
          throw new Error(404)
        }
        text = prepareMarkdownContent(text, fileDir)

        if (this.md) {
          this.html = this.md.render(text)
        }
      } catch (error) {
        globalErrorHandler(error)
      } finally {
        this.loading = false
      }
    },
    async getDocument(path = this.currentDocument) {
      removeClass(`${className}-toc-list-level--active`)
      removeClass(`${className}-toc-list-level--open`)

      if (!isDocumentAvailable(this.levels, path)) {
        routerParamsReplace({ ...this.defaultDocument })

        return
      }

      await this.render(path.category, path.file)

      const element = document.getElementById(`${className}-category-${path.category}-file-${path.file}`)
      if (element) {
        element.classList.add(`${className}-toc-list-level--active`)
        element.classList.add(`${className}-toc-list-level--open`)
      }

      await this.$nextTick()
      this.scrollTo()
    },

    onScroll() {
      if (!this.$refs.body) {
        return
      }

      let maxMin
      let current

      const doStuff = (element, callback) => {
        callback(element)

        if (element.parentElement) {
          switch (element.parentElement.nodeName.toLowerCase()) {
            case 'ul': {
              if (element.parentElement.parentElement && element.parentElement.parentElement.nodeName.toLowerCase() === 'li') {
                doStuff(element.parentElement.parentElement, callback)
              }
              break
            }
            case 'li': {
              doStuff(element.parentElement, callback)
              break
            }
          }
        }
      }

      const prefix = `${className}-toc-list-level`
      this.slugs.forEach(slug => {
        const link = document.getElementById(slug)
        if (link) {
          const header = document.getElementById(`${prefix}-${slug}`)
          const bodyRect = this.$refs.body.getBoundingClientRect()
          const linkRect = link.getBoundingClientRect()
          const offset = linkRect.top - bodyRect.top - 32

          if (header && header.classList && header.matches(`.${prefix}--open`)) {
            header.classList.remove(`${prefix}--active`)
            header.classList.remove(`${prefix}--open`)
          }

          if (maxMin === undefined || offset < 0 && offset > maxMin) {
            maxMin = offset
            current = header
          }
        }
      })

      if (current) {
        doStuff(current, element => {
          element.classList.add(`${prefix}--active`)
          element.classList.add(`${prefix}--open`)
        })
      }
    },

    scrollTo(id) {
      id = this.$route.query.id || id
      const element = document.getElementById(id)
      const body = this.$refs.body

      if (!body) {
        return
      }

      if (element) {
        const containerRect = body.getBoundingClientRect()
        const elementRect = element.getBoundingClientRect()
        const scrollTop = body.scrollTop + (elementRect.top - containerRect.top)

        body.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        })
      } else {
        body.scrollTo({
          top: 0,
          behavior: 'smooth'
        })
      }
    }
  },

  render
}
